import { SSOProtocol, type DomainSSOConfiguration } from './ssoTypes'

/**
 * SAML Test Mode Configuration
 * Allows testing the SAML flow with either:
 * 1. Production Lambda (default) - Direct test of real backend
 * 2. Local Lambda - For offline development
 */
export const SAML_TEST_CONFIG = {
  enabled: process.env.NODE_ENV !== 'production',
  
  // Production Lambda endpoint (default for test mode)
  productionLambdaUrl: 'https://sso.dopa.ge/auth/saml/callback',
  
  // Local Lambda endpoint (fallback)
  localLambdaUrl: 'http://localhost:3020/saml',
  
  // Mock SAML response with test user data
  mockSAMLResponse: 'PHNhbWxwOlJlc3BvbnNlIHhtbG5zOnNhbWxwPSJ1cm46b2FzaXM6bmFtZXM6dGM6U0FNTDoyLjA6cHJvdG9jb2wiIElEPSJfdGVzdDEyMyIgVmVyc2lvbj0iMi4wIj48c2FtbDpJc3N1ZXIgeG1sbnM6c2FtbD0idXJuOm9hc2lzOm5hbWVzOnRjOlNBTUw6Mi4wOmFzc2VydGlvbiI+aHR0cDovL3Rlc3QuY29tPC9zYW1sOklzc3Vlcj48c2FtbHA6U3RhdHVzPjxzYW1scDpTdGF0dXNDb2RlIFZhbHVlPSJ1cm46b2FzaXM6bmFtZXM6dGM6U0FNTDoyLjA6c3RhdHVzOlN1Y2Nlc3MiLz48L3NhbWxwOlN0YXR1cz48c2FtbDpBc3NlcnRpb24geG1sbnM6c2FtbD0idXJuOm9hc2lzOm5hbWVzOnRjOlNBTUw6Mi4wOmFzc2VydGlvbiI+PHNhbWw6U3ViamVjdD48c2FtbDpOYW1lSUQ+c2hhbmVibGFzZXJAZ21haWwuY29tPC9zYW1sOk5hbWVJRD48L3NhbWw6U3ViamVjdD48c2FtbDpBdHRyaWJ1dGVTdGF0ZW1lbnQ+PHNhbWw6QXR0cmlidXRlIE5hbWU9ImVtYWlsIj48c2FtbDpBdHRyaWJ1dGVWYWx1ZT5zaGFuZWJsYXNlckBnbWFpbC5jb208L3NhbWw6QXR0cmlidXRlVmFsdWU+PC9zYW1sOkF0dHJpYnV0ZT48c2FtbDpBdHRyaWJ1dGUgTmFtZT0iZmlyc3ROYW1lIj48c2FtbDpBdHRyaWJ1dGVWYWx1ZT5TaGFuZTwvc2FtbDpBdHRyaWJ1dGVWYWx1ZT48L3NhbWw6QXR0cmlidXRlPjxzYW1sOkF0dHJpYnV0ZSBOYW1lPSJsYXN0TmFtZSI+PHNhbWw6QXR0cmlidXRlVmFsdWU+Qmxhc2VyPC9zYW1sOkF0dHJpYnV0ZVZhbHVlPjwvc2FtbDpBdHRyaWJ1dGU+PC9zYW1sOkF0dHJpYnV0ZVN0YXRlbWVudD48L3NhbWw6QXNzZXJ0aW9uPjwvc2FtbHA6UmVzcG9uc2U+Cg==',
  
  // Get appropriate relay state based on environment
  getRelayState: (): string => {
    const origin = window.location.origin
    return `${origin}/auth/saml/callback`
  }
}

const domainSSOConfigurations: Record<string, DomainSSOConfiguration> = {
  unl: {
    domain: 'unl.dopa.ge',
    defaultAuthMethod: 'sso',
    ssoConfig: {
      protocol: SSOProtocol.SAML2,
      enabled: true,
      displayName: 'UNL SSO',
      idpUrl: 'https://shib.unl.edu/idp/profile/SAML2/Redirect/SSO',
      entityId: 'https://sso.dopa.ge',
      acsUrl: 'https://sso.dopa.ge/auth/saml/callback'
    }
  },
  studio: {
    domain: 'studio.dopa.ge',
    defaultAuthMethod: 'password',
    ssoConfig: {
      protocol: SSOProtocol.OIDC,
      enabled: true,
      displayName: 'Okta SSO',
      authority: 'https://dev-78829499.okta.com/oauth2/default',
      clientId: '0oapjq3uymzq79ArT5d7',
      redirectUri: `${window.location.origin}/auth/sso/callback`,
      scopes: ['email', 'openid', 'profile'],
      responseType: 'code'
    }
  }
  // Additional domains can be added here
}

export const getDomainSSOConfig = (domain: string): DomainSSOConfiguration | null => {
  return domainSSOConfigurations[domain] ?? null
}

export const getDefaultAuthMethod = (domain: string): 'sso' | 'password' => {
  const config = getDomainSSOConfig(domain)
  return config?.defaultAuthMethod ?? 'password'
}

export const isSSOEnabledForDomain = (domain: string): boolean => {
  const config = getDomainSSOConfig(domain)
  return config?.ssoConfig?.enabled ?? false
}

